import React from 'react';
import { NavLink, RouteComponentProps } from 'react-router-dom';
import withBreadcrumbs, { BreadcrumbsRoute } from 'react-router-breadcrumbs-hoc';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles } from 'rewire-ui';
import Button from '@material-ui/core/Button';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Routes from '../Routes';
import mainPageModel from './MainPageModel';
import scheduleControlsModel from '../schedule/ScheduleControlsModel';
import breadcrumbsModel from './BreadcrumbsModel';
import { RouteManagerBase } from './RouteManager';

const styles = (theme: Theme) => ({
  root: {
    [theme.breakpoints.down('md')]: {
      width: 'auto',
    },
    marginBottom: '7px',
    marginRight: '20px',
    flexGrow: 0,
  },
  link: {
    color: 'inherit',
    textDecoration: 'none',
  },
  linkActive: {
    fontWeight: 'bold',
  },
  separator: {
    margin: '0px 6px',
  },
  advancedSearchContainer: {
    display: 'inline-block',
  },
  advancedSearchSaveButton: {
    marginLeft: '8px',
    textTransform: 'none',
    minWidth: '0px',
    flex: '0 0 auto',
  },
});

const AreaBreadcrumb = ({ match }: RouteComponentProps<any>) => {
  const area = mainPageModel.areaMap[match.params.areaId];
  const areaName = area?.name;

  return <span>{areaName || 'No Area'}</span>;
};

const GroupBreadcrumb = ({ match }: RouteComponentProps<any>) => {
  const group = mainPageModel.groupMap[match.params.groupId];
  const groupName = group?.name;

  return <span>{groupName || 'No Group'}</span>;
};

const SearchBreadcrumb = () => <span style={{ fontStyle: 'italic' }}>Search</span>;

const customRoutes: BreadcrumbsRoute[] = [
  { path: Routes.Root, breadcrumb: undefined },
  { path: Routes.Home, breadcrumb: undefined },
  { path: Routes.Areas, breadcrumb: undefined },
  { path: Routes.Groups, breadcrumb: undefined },
  { path: Routes.SearchBase, breadcrumb: undefined },
  { path: Routes.SearchAreaBase, breadcrumb: undefined },
  { path: Routes.SearchGroupBase, breadcrumb: undefined },
  { path: Routes.Area, breadcrumb: AreaBreadcrumb },
  { path: Routes.Group, breadcrumb: GroupBreadcrumb },
  { path: Routes.JobSchedule, breadcrumb: '' },
  { path: Routes.EmployeeSchedule, breadcrumb: '' },
  { path: Routes.TimeOffCalendar, breadcrumb: '' },
  { path: Routes.TimeOffList, breadcrumb: '' },
  { path: Routes.Timecard, breadcrumb: '' },
  { path: Routes.TimecardSummary, breadcrumb: '' },
  { path: Routes.TimecardTest, breadcrumb: '' },
  { path: Routes.Batch, breadcrumb: '' },
  { path: Routes.BatchSummary, breadcrumb: '' },
  { path: Routes.Report, breadcrumb: '' },
  { path: Routes.Analytics, breadcrumb: undefined },
  { path: Routes.Search, breadcrumb: SearchBreadcrumb },
  { path: Routes.SearchArea, breadcrumb: SearchBreadcrumb },
  { path: Routes.SearchGroup, breadcrumb: SearchBreadcrumb },
];

interface IBreadcrumbsProps {
  classes: any;
  breadcrumbs: any[];
}

type BreadcrumbsProps = WithStyle<ReturnType<typeof styles>, IBreadcrumbsProps>;

class Breadcrumbs extends React.Component<BreadcrumbsProps> {
  constructor(props: BreadcrumbsProps) {
    super(props);
  }

  isActive = (toUrl: string) => (match: any, location: any) => {
    return location.pathname === toUrl;
  };

  renderSeparator(classes: any) {
    return <span className={classes.separator}>»</span>;
  }

  renderAreaGroup(classes: any, breadcrumb: any, areaId?: string, groupId?: string) {
    if (!areaId || !groupId) return null;

    const area = mainPageModel.areaMap[areaId];
    const areaName = area?.name;
    const group = mainPageModel.groupMap[groupId];
    const groupName = group?.name;
    if (!areaName || !groupName) return null;

    const areaUrl = Routes.toArea(areaId);
    const groupUrl = RouteManagerBase.current.createPayPlanLinkFor({ areaId, groupId });
    return (
      <span key={groupUrl}>
        <span key={areaUrl}>
          <NavLink exact className={classes.link} activeClassName={classes.linkActive} to={areaUrl} isActive={() => false}>
            {areaName}
          </NavLink>
        </span>
        {this.renderSeparator(classes)}
        <span key={groupUrl}>
          <NavLink exact className={classes.link} activeClassName={classes.linkActive} to={groupUrl} isActive={() => false}>
            {groupName}
          </NavLink>
        </span>
      </span>
    );
  }

  renderView(classes: any, breadcrumb: any, viewId?: string) {
    if (!viewId) return null;

    const view = mainPageModel.viewMap[viewId];
    const viewName = view?.name;
    if (!viewName) return null;

    const viewUrl = RouteManagerBase.current.createPayPlanLinkFor({ viewId });
    return (
      <span key={viewUrl}>
        <NavLink exact className={classes.link} activeClassName={classes.linkActive} to={viewUrl} isActive={() => false}>
          {viewName}
        </NavLink>
      </span>
    );
  }

  renderAdvancedSearch(classes: any, breadcrumb: any, groupId?: string, viewId?: string, search?: string) {
    const key = breadcrumb.key;
    const baseUrl = breadcrumb.match.url;

    const addAdvancedSearch = (!groupId && !viewId) || search;
    if (!addAdvancedSearch) {
      return null;
    }

    let toUrl = baseUrl;
    if (location.search) {
      toUrl += `${location.search}`;
    }

    return (
      <span className={classes.advancedSearchContainer} key={key}>
        <NavLink exact className={classes.link} activeClassName={classes.linkActive} to={toUrl} isActive={() => true}>
          Advanced Search
        </NavLink>
        <Button
          variant="contained"
          size="small"
          className={classes.advancedSearchSaveButton}
          onClick={() => scheduleControlsModel.showSearchViewDialog()}
        >
          Save
        </Button>
      </span>
    );
  }

  renderPayPlanBreadcrumbs = React.memo(
    ({
      classes,
      breadcrumbs,
      search,
      areaId,
      groupId,
      viewId,
    }: { classes: any; breadcrumbs: any; search?: string; areaId?: string; groupId?: string; viewId?: string }) => {
      const renderedBreadcrumbs: any[] = [];

      renderedBreadcrumbs.push([
        this.renderAdvancedSearch(classes, breadcrumbs[breadcrumbs.length - 1], groupId, viewId, search) ??
          this.renderAreaGroup(classes, breadcrumbs[0], areaId, groupId) ??
          this.renderView(classes, breadcrumbs[0], viewId),
      ]);

      if (renderedBreadcrumbs.length === 0 || !renderedBreadcrumbs[0]) return null;

      return (
        <Typography variant="body2" className={classes.root}>
          {renderedBreadcrumbs}
        </Typography>
      );
    },
  );

  render() {
    const { classes, breadcrumbs } = this.props;

    return (
      <Observe
        render={() => {
          const { areaId, groupId, viewId, search } = breadcrumbsModel;
          const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
          if (
            lastBreadcrumb &&
            (lastBreadcrumb.match.url === Routes.JobSchedule ||
              lastBreadcrumb.match.url === Routes.EmployeeSchedule ||
              lastBreadcrumb.match.url === Routes.TimeOffCalendar ||
              lastBreadcrumb.match.url === Routes.TimeOffList ||
              lastBreadcrumb.match.url === Routes.Timecard ||
              lastBreadcrumb.match.url === Routes.TimecardSummary ||
              lastBreadcrumb.match.url === Routes.TimecardTest ||
              lastBreadcrumb.match.url === Routes.Batch ||
              lastBreadcrumb.match.url === Routes.BatchSummary ||
              lastBreadcrumb.match.url === Routes.Report)
          ) {
            return (
              <this.renderPayPlanBreadcrumbs
                classes={classes}
                breadcrumbs={breadcrumbs}
                search={search}
                areaId={areaId}
                groupId={groupId}
                viewId={viewId}
              />
            );
          }

          return (
            <Typography variant="body2" className={classes.root}>
              {breadcrumbs.map(({ key, match, breadcrumb, location }: any, index: number) => {
                const baseUrl = match.url;
                let toUrl = baseUrl;
                if ((toUrl === Routes.JobSchedule || toUrl === Routes.EmployeeSchedule) && location.search) {
                  toUrl += `${location.search}`;
                }
                return (
                  <span key={key}>
                    <NavLink
                      exact
                      className={classes.link}
                      activeClassName={classes.linkActive}
                      to={toUrl}
                      isActive={this.isActive(baseUrl)}
                    >
                      {breadcrumb}
                    </NavLink>
                    {index < breadcrumbs.length - 1 && this.renderSeparator(classes)}
                  </span>
                );
              })}
            </Typography>
          );
        }}
      />
    );
  }
}

export default withBreadcrumbs(customRoutes)(withStyles(styles, Breadcrumbs));
